import { ReactElement } from 'react'
import { NavLink, useLocation, matchPath } from 'react-router-dom'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import cn from 'classnames'
import styles from '../SideMenuClient.module.scss'
import { IMenuData } from '../SideMenuConfig'
import ExpandableMenuItem from './ExpandableNavItem'

interface IProps {
  sideMenuItems: IMenuData[]
  isSidebarOpen: boolean
  expandableListItemsState: { [key: string]: boolean }
  handleItemExpand: (itemName: string) => void
  toggleSidebar?: () => void
}

const SideMenuItems = ({
  sideMenuItems,
  isSidebarOpen,
  expandableListItemsState,
  handleItemExpand,
  toggleSidebar,
}: IProps) => {
  const location = useLocation()

  const isCurrentPage = (link: string) => {
    return matchPath(
      {
        path: location.pathname,
        end: true,
      },
      link,
    )
  }

  const groupedItems: { [key: string]: IMenuData[] } = {}
  const result: ReactElement[] = []

  sideMenuItems.forEach((item) => {
    const category = item.category || ''
    if (!groupedItems[category]) {
      groupedItems[category] = []
    }
    groupedItems[category].push(item)
  })

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Object.entries(groupedItems).forEach(([_, items]) => {
    items.forEach(({ icon, label, subItems, link, activeIcon, divider }: IMenuData) => {
      result.push(
        <div key={link} className={styles.sideMenuItem}>
          {subItems ? (
            <ExpandableMenuItem
              icon={icon}
              label={label}
              subItems={subItems}
              handleItemExpand={handleItemExpand}
              itemExpanded={expandableListItemsState[label]}
              sidebarExpanded={isSidebarOpen}
              location={location}
              toggleSidebar={toggleSidebar}
            />
          ) : (
            <>
              {divider && <div className={styles.divider} />}
              <NavLink
                to={link}
                className={({ isActive }) => (isActive ? styles.active : '')}
                onClick={() => {
                  if (toggleSidebar) {
                    toggleSidebar()
                  }
                  // Prevent default scroll behavior on mobile
                  if (window.innerWidth < 960) {
                    window.scrollTo(0, 0)
                  }
                }}
              >
                <ListItemButton
                  className={styles.navItem}
                  disableRipple
                  data-cy={`menu-item-${label}`}
                >
                  <div className={styles.collapsedIcon}>
                    <ListItemIcon
                      className={cn(styles.icon, {
                        [styles.colorIcon]: !activeIcon,
                      })}
                    >
                      {activeIcon && isCurrentPage(link) ? activeIcon : icon}
                    </ListItemIcon>
                  </div>

                  {isSidebarOpen && (
                    <ListItemText classes={{ primary: styles.label }} primary={label} />
                  )}
                </ListItemButton>
              </NavLink>
            </>
          )}
        </div>,
      )
    })
  })

  return <>{result}</>
}

export default SideMenuItems
